/**
 * Permission guards for the marketing live embed (P6-C).
 *
 * The embed renders the REAL app, which on a normal load proactively asks for
 * microphone (inbound calls), geolocation (canvassing map), and notifications.
 * Inside a marketing iframe that means a visitor scrolling the homepage gets
 * hit with native permission prompts — unacceptable. We neuter every prompt at
 * the API level so nothing can pop, no matter which code path reaches for it.
 * (The iframe also carries `allow=""`, and the app skips its pre-prompt flow in
 * demo mode — this is the belt to those suspenders.)
 *
 * Must run BEFORE any app code executes — call it at the very top of main.tsx.
 */
export function isDemoEmbedContext(): boolean {
  try {
    const u = new URL(window.location.href);
    return u.pathname.startsWith("/demo-embed") || u.searchParams.get("demoEmbed") === "1";
  } catch {
    return false;
  }
}

export function installDemoEmbedGuards(): void {
  if (typeof window === "undefined" || !isDemoEmbedContext()) return;

  // Geolocation — never prompt for "call location". Fire the error callback so
  // callers fail gracefully instead of hanging.
  try {
    const geo = navigator.geolocation;
    if (geo) {
      geo.getCurrentPosition = ((_ok: unknown, err?: (e: unknown) => void) => {
        if (typeof err === "function") err({ code: 1, message: "demo: denied" });
      }) as typeof geo.getCurrentPosition;
      geo.watchPosition = (() => 0) as typeof geo.watchPosition;
      geo.clearWatch = (() => undefined) as typeof geo.clearWatch;
    }
  } catch { /* ignore */ }

  // Notifications — never prompt, always report denied.
  try {
    const N = (window as unknown as { Notification?: { requestPermission?: unknown; permission?: string } }).Notification;
    if (N) {
      N.requestPermission = (() => Promise.resolve("denied")) as unknown as typeof N.requestPermission;
    }
  } catch { /* ignore */ }

  // Microphone / camera — never prompt.
  try {
    if (navigator.mediaDevices) {
      navigator.mediaDevices.getUserMedia = (() =>
        Promise.reject(new DOMException("Demo mode — media disabled", "NotAllowedError"))) as typeof navigator.mediaDevices.getUserMedia;
    }
  } catch { /* ignore */ }

  // Permissions API — report denied so callers skip the prompt entirely.
  try {
    if (navigator.permissions?.query) {
      navigator.permissions.query = (() =>
        Promise.resolve({ state: "denied", onchange: null } as unknown as PermissionStatus)) as typeof navigator.permissions.query;
    }
  } catch { /* ignore */ }
}
